import { useCallback } from 'react';
import api from '../../services/api.js';

/**
 * Hook para operações CRUD básicas de artigos
 * @param {Function} setArticles - Função para atualizar o estado dos artigos
 * @param {Function} showSnackbar - Função para exibir notificações
 * @param {Function} cleanupSelection - Função para limpar IDs inexistentes da seleção
 * @returns {Object} Objeto com funções de CRUD (deleteArticle, updateArticleInState, removeArticleFromState)
 */
const useArticleCRUD = (setArticles, showSnackbar, cleanupSelection) => {
  // Notificação
  const notify = useCallback((message, severity = 'success') => {
    if (typeof showSnackbar === 'function') {
      showSnackbar(message, severity);
    }
  }, [showSnackbar]);

  // Atualizar artigo no estado local
  const updateArticleInState = useCallback((articleId, updaterFn) => {
    setArticles(prevArticles =>
      prevArticles.map(article =>
        article._id === articleId
          ? updaterFn(article)
          : article
      )
    );
  }, [setArticles]);

  // Remover artigo do estado local
  const removeArticleFromState = useCallback((articleId) => {
    setArticles(prevArticles =>
      prevArticles.filter(article => article._id !== articleId)
    );
  }, [setArticles]);

  // Excluir artigo
  const deleteArticle = useCallback(async (id) => {
    try {
      await api.delete(`/articles/${id}`);

      // Atualizar o estado dos artigos primeiro (igual ao deleteArticlesBulk)
      setArticles(prevArticles => {
        const updatedArticles = prevArticles.filter(article => article._id !== id);

        // Limpar IDs excluídos da seleção imediatamente (igual ao deleteArticlesBulk)
        if (cleanupSelection) {
          const validIds = updatedArticles.map(article => article._id);
          cleanupSelection(validIds);
        }

        return updatedArticles;
      });

      notify('Artigo excluído com sucesso');
      return true;
    } catch (err) {
      console.error('Erro ao excluir artigo:', err);

      // Se o erro for 404, o artigo já foi excluído por outro processo
      if (err.response && err.response.status === 404) {
        setArticles(prevArticles => {
          const updatedArticles = prevArticles.filter(article => article._id !== id);
          if (cleanupSelection) {
            const validIds = updatedArticles.map(article => article._id);
            cleanupSelection(validIds);
          }
          return updatedArticles;
        });
        notify('Artigo já havia sido excluído (removido da lista)', 'warning');
        return true;
      }

      notify('Erro ao excluir artigo', 'error');
      return false;
    }
  }, [setArticles, notify, cleanupSelection]);

  // Toggle Favorite com optimistic update
  const toggleFavorite = useCallback(async (articleId) => {
    // 1. Atualizar UI imediatamente (optimistic)
    updateArticleInState(articleId, article => ({
      ...article,
      isFavorite: !article.isFavorite
    }));

    try {
      // 2. Chamar API em background
      await api.put(`/articles/${articleId}/favorite`);
    } catch (error) {
      // 3. Reverter em caso de erro
      console.error('Erro ao alternar favorito:', error);
      updateArticleInState(articleId, article => ({
        ...article,
        isFavorite: !article.isFavorite
      }));
      notify('Erro ao atualizar favorito', 'error');
    }
  }, [updateArticleInState, notify]);

  // Toggle Read com optimistic update
  const toggleRead = useCallback(async (articleId) => {
    // 1. Atualizar UI imediatamente (optimistic)
    updateArticleInState(articleId, article => ({
      ...article,
      isRead: !article.isRead
    }));

    try {
      // 2. Chamar API em background
      await api.put(`/articles/${articleId}/read`);
    } catch (error) {
      // 3. Reverter em caso de erro
      console.error('Erro ao alternar lido:', error);
      updateArticleInState(articleId, article => ({
        ...article,
        isRead: !article.isRead
      }));
      notify('Erro ao atualizar status de leitura', 'error');
    }
  }, [updateArticleInState, notify]);

  // Update Sentiment com optimistic update e propagação para similares
  const updateSentiment = useCallback(async (articleId, sentiment, propagate = true) => {
    // Guardar sentimento anterior para rollback
    let previousSentiment = null;

    // 1. Atualizar UI imediatamente (optimistic)
    updateArticleInState(articleId, article => {
      previousSentiment = article.sentiment;
      return {
        ...article,
        sentiment,
        sentimentManual: true
      };
    });

    try {
      // 2. Chamar API em background com propagação
      const response = await api.put(`/articles/${articleId}/sentiment`, {
        sentiment,
        propagate,
        minScore: 90 // Score mínimo para considerar artigos similares
      });

      // 3. Se houve propagação, atualizar artigos similares no estado local
      if (response.data?.data) {
        updateArticleInState(articleId, article => ({
          ...article,
          ...response.data.data
        }));
      }

      if (response.data?.propagation?.articles?.length > 0) {
        const similarIds = response.data.propagation.articles.map(a => a._id);
        setArticles(prevArticles =>
          prevArticles.map(article =>
            similarIds.includes(article._id)
              ? { ...article, sentiment, sentimentManual: true }
              : article
          )
        );

        const count = response.data.propagation.updated;
        if (count > 0) {
          notify(`Sentimento aplicado a ${count} artigo${count > 1 ? 's' : ''} similar${count > 1 ? 'es' : ''}`, 'success');
        }
      }
    } catch (error) {
      // 4. Reverter em caso de erro
      console.error('Erro ao atualizar sentimento:', error);
      updateArticleInState(articleId, article => ({
        ...article,
        sentiment: previousSentiment
      }));
      notify('Erro ao atualizar sentimento', 'error');
    }
  }, [updateArticleInState, setArticles, notify]);

  // TODO: Adicionar createArticle e updateArticle se necessário

  return {
    deleteArticle,
    updateArticleInState,
    removeArticleFromState,
    toggleFavorite,
    toggleRead,
    updateSentiment
  };
};

export default useArticleCRUD; 
